import { NavLink, Outlet, useNavigate } from "react-router";
import { useStaffAuth } from "@/lib/use-staff-auth";
import { useStaffCompanies, useStaffQueue } from "@/lib/staff-hooks";
import { STAFF_ROLE_LABELS, staffCan } from "@/lib/staff-capabilities";
import { COMPANIES_PHRASE, navTabsFor, TAB_COUNT_PHRASES } from "@/lib/staff-tabs";
import type { NavTab } from "@/lib/staff-tabs";
import type { StaffQueueCounts, StaffRole } from "@/lib/staff-api";
import { StaffSearchBox } from "./StaffSearchBox";

function countBadge(n: number, phrase: string): React.ReactElement {
  return (
    <>
      <span aria-hidden="true" title={phrase} className="ml-2 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-700">
        {n}
      </span>
      <span className="sr-only">{phrase}</span>
    </>
  );
}

function tabCount(tab: NavTab, counts: StaffQueueCounts | undefined, companies: number | undefined): React.ReactElement | null {
  if (tab.key === "companies") return companies === undefined ? null : countBadge(companies, COMPANIES_PHRASE(companies));
  if (tab.key === "today" || tab.key === "payments" || !counts) return null;
  const n = counts[tab.key];
  return countBadge(n, TAB_COUNT_PHRASES[tab.key](n));
}

function DashboardTabs({ role }: { role: StaffRole }): React.ReactElement {
  // The queue counts come from Today's query (it fetches only for queue
  // workers); the Companies count from the Companies list's total. That list
  // is asked without includeHidden, so hidden companies are never counted.
  const { data } = useStaffQueue("today");
  const companies = useStaffCompanies();
  const counts = data?.meta.counts;

  return (
    <nav aria-label="Dashboard tabs" className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-4">
      {navTabsFor(role).map((tab) => (
        <NavLink
          key={tab.key}
          to={tab.path}
          end={tab.path === "/staff"}
          className={({ isActive }) =>
            `whitespace-nowrap border-b-2 px-3 py-2 text-sm ${
              isActive ? "border-blue-600 font-semibold text-gray-900" : "border-transparent text-gray-600 hover:text-gray-900"
            }`
          }
        >
          <span>{tab.label}</span>
          {tabCount(tab, counts, companies.data?.total)}
        </NavLink>
      ))}
    </nav>
  );
}

/** The staff shell: who is signed in, sign-out, the search box, and the tabs
 *  for the signed-in role. Renders outside the vendor Layout. */
export function StaffLayout(): React.ReactElement {
  const { staff, logout } = useStaffAuth();
  const navigate = useNavigate();

  const signOut = (): void => {
    logout();
    void navigate("/staff/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <span className="text-xl font-bold text-gray-900">VendorPay Staff</span>
          {staff && staffCan(staff.role, "view_directory") && <StaffSearchBox />}
          {staff && (
            <div className="flex items-center gap-4 text-sm">
              <span className="text-right">
                <span className="block font-medium text-gray-900">
                  {staff.firstName} {staff.lastName}
                </span>
                <span className="block text-xs text-gray-500">{STAFF_ROLE_LABELS[staff.role]}</span>
              </span>
              <button type="button" onClick={signOut} className="font-medium text-gray-500 hover:text-gray-700">
                Sign out
              </button>
            </div>
          )}
        </div>
        {staff && <DashboardTabs role={staff.role} />}
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8">
        <Outlet />
      </main>
    </div>
  );
}
